'use client';

import * as React from 'react';
import type { InputFieldBaseProps, InputFieldProps } from './type';
import { Field } from '../Form/Field/Field';
import { Input } from '../Input/Input';
import { css } from '@mildang/styled-system/css';
import { useReactFormPropsMapper } from '../Form/lib/useReactFormPropsMapper';

/**
 * Input 라벨 필드(프레젠테이션). 폼 바인딩 없이 값을 직접 다룰 때 쓴다.
 * 라벨/도움말 배치·접근성은 Field가 단일 소스로 처리하고, 여기서는 Input과 adornment 배치·id 연결만 담당한다.
 */
export const InputFieldBase = React.forwardRef<HTMLInputElement, InputFieldBaseProps>((props, ref) => {
  const {
    label,
    helperText,
    description,
    className,
    id,
    _containerId,
    outEndAdornment,
    sideContent,
    hideHelperText,
    ...rest
  } = props;

  const defaultId = React.useId();
  const resolvedId = id ?? defaultId;
  const helpId = `${resolvedId}-help`;
  const shownHelperText = hideHelperText ? undefined : helperText;
  const variant = rest.error ? 'error' : rest.warning ? 'warning' : rest.success ? 'success' : 'info';

  // Input에 id/aria를 직접 명시 → Field의 htmlFor로 자동주입을 끄고(adornment wrapper 오주입 방지),
  // 라벨↔컨트롤 연결만 Field가 처리.
  const input = (
    <Input
      ref={ref}
      id={resolvedId}
      aria-describedby={shownHelperText ? helpId : undefined}
      _containerId={_containerId}
      {...rest}
    />
  );

  return (
    <Field
      className={className}
      fullWidth={rest.fullWidth}
      label={label}
      description={description}
      sideContent={sideContent}
      required={rest.required}
      helpText={shownHelperText}
      helpVariant={variant}
      htmlFor={resolvedId}
    >
      {outEndAdornment ? (
        <div className={css({ display: 'flex', gap: '8' })}>
          {input}
          {outEndAdornment}
        </div>
      ) : (
        input
      )}
    </Field>
  );
});

InputFieldBase.displayName = 'InputFieldBase';

/**
 * Input 폼 필드(완성형). form.AppField 안에서 field.InputField로 사용한다.
 * 폼 컨텍스트 없이 단독 렌더링하면 오류가 발생하므로, 독립 검색창에는 InputFieldBase나 Input을 사용한다.
 * InputFieldBase에 폼 필드 바인딩을 얹는다.
 * (CheckboxField/SelectField와 동일하게 Base와 같은 파일에 co-locate.)
 */
export const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>((props, ref) => {
  const { helperText, ...rest } = props;

  // mapper는 유효할 때 raw helperText를, 유효하지 않을 때 필드 에러 메시지를 helperText로 돌려준다.
  const { error, id, name, ...otherProps } = useReactFormPropsMapper<string>({ helperText });

  return (
    <InputFieldBase
      ref={ref}
      error={error}
      id={id}
      name={name}
      {...otherProps}
      {...rest}
      disabled={rest.disabled || otherProps.disabled}
    />
  );
});

InputField.displayName = 'InputField';
